import React from 'react';
import styled from 'styled-components';
import { IClientContact, ISupplierContact } from '@bb/contacts';

import { AddressComponent, Contact, Divider, Text, theme } from 'components';
import {
  capitalize,
  featureEnabled,
  formatPhoneNumber,
  formatPhoneNumberAsLink,
} from 'utils';

import { Container, Section, TouchableValue } from './common';
import Row from './Row';
import { ContactDrawerType } from '../../types';

interface ContactDetailsProps {
  contact: IClientContact | ISupplierContact;
  linkedContacts: IClientContact[];
  canResendClientInvitation: boolean;
  goToContact: (type: ContactDrawerType, contactId: string) => void;
  resendClientInvitation: (clientId: string) => Promise<void>;
}

const ContactDetails: React.FC<ContactDetailsProps> = ({
  contact,
  linkedContacts,
  canResendClientInvitation,
  goToContact,
  resendClientInvitation,
}) => (
  <Container>
    <Section data-cy="contacts-details-contactMethod">
      <Row label="Email">
        <TouchableValue href={`mailto:${contact.email}`}>
          {contact.email}
        </TouchableValue>
      </Row>
      {contact.phones.map(({ type, number }, i) => (
        <Row label={capitalize(type)} key={i}>
          <TouchableValue href={formatPhoneNumberAsLink(number)}>
            {formatPhoneNumber(number)}
          </TouchableValue>
        </Row>
      ))}
      {contact.type === 'supplier' && contact.website ? (
        <Row label="Website" value={contact.website} />
      ) : null}
      {canResendClientInvitation && contact.id && (
        <Row label="Dashboard">
          <TouchableValue onClick={() => resendClientInvitation(contact.id)}>
            Send Invitation
          </TouchableValue>
        </Row>
      )}
    </Section>
    {contact.type === 'client' && contact.address && (
      <>
        <Divider />
        <Section data-cy="contact-details-homeAddress">
          <Row
            label="Home"
            value={
              <AddressComponent address={contact.address} displayIcon={false} />
            }
          />
        </Section>
      </>
    )}
    {linkedContacts.length ? (
      <>
        <Divider />
        <Section data-cy="contact-details-linkedContacts">
          <Row
            label="Linked"
            value={
              <StyledLinkedContactsContainer>
                {linkedContacts.map(linkedContact => {
                  const { id, firstName, lastName } = linkedContact;

                  return (
                    <Contact
                      key={id}
                      contact={{
                        id: id!,
                        firstName,
                        lastName,
                      }}
                      avatarSize={25}
                      loading={false}
                      onClick={() => goToContact(ContactDrawerType.Contact, id)}
                    />
                  );
                })}
              </StyledLinkedContactsContainer>
            }
          />
        </Section>
      </>
    ) : null}
    {contact.notes && (
      <>
        <Divider />
        <Section data-cy="contact-details-notes">
          <Row label="Notes" value={contact.notes} />
        </Section>
      </>
    )}
  </Container>
);

export default ContactDetails;

const StyledLinkedContactsContainer = styled.div`
  display: grid;
  gap: 8px;

  ${Text} {
    color: ${theme.blue6};
  }
`;
